import { useState } from 'react';
import { useGameContext } from '@/state/GameContext';
import { RARITY_COLORS } from '@/state/settings';
import { ItemIcon } from './ItemIcon';
import { reconstructGrid } from './gridUtils';
import type { GeneratedItem, ItemId } from '@/types/domain';

export function RecipeBook() {
  const game = useGameContext();
  const [selected, setSelected] = useState<ItemId | null>(null);
  const list = game.discovered;

  const sel = list.find((d) => d.id === selected) || null;

  if (sel) return <BookDetails item={sel} onBack={() => setSelected(null)} />;

  return (
    <div className="panel-body" style={{ padding: 0 }}>
      <div className="muted" style={{ padding: '8px 10px 0' }}>{list.length} discovered custom item{list.length === 1 ? '' : 's'}</div>
      {list.length === 0 ? <div className="muted" style={{ padding: 12 }}>Craft something to discover your first custom item.</div> : null}
      {list.map((d) => (
        <div key={d.id} className="list-item" onClick={() => setSelected(d.id)}>
          <div className="ico"><ItemIcon itemId={d.id} size={32} showTooltip={false} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="li-name" style={{ color: RARITY_COLORS[d.rarity] || '#fff' }}>{d.name}</div>
            <div className="li-sub">Tier {d.tier} &middot; {d.category} &middot; {d.tags.slice(0, 3).join(', ')}</div>
          </div>
          <MiniGrid item={d} />
        </div>
      ))}
    </div>
  );
}

function MiniGrid({ item }: { item: GeneratedItem }) {
  const game = useGameContext();
  const grid = reconstructGrid(item);
  return (
    <div className="mini-grid" title={item.recipeType}>
      {grid.map((id, i) => (
        <div key={i} className="mc">
          {id ? <img src={game.urlFor(id)} alt="" /> : null}
        </div>
      ))}
    </div>
  );
}

function BookDetails({ item, onBack }: { item: GeneratedItem; onBack: () => void }) {
  const game = useGameContext();
  const children = game.discovered.filter((d) => d.ingredientIds.includes(item.id));
  const parents = item.ingredientIds.map((id) => game.metaFor(id)).filter((m): m is NonNullable<typeof m> => !!m);

  return (
    <div className="panel-body">
      <button className="btn ghost" onClick={onBack}>&larr; Back</button>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '12px 0' }}>
        <ItemIcon itemId={item.id} size={56} showTooltip={false} />
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: RARITY_COLORS[item.rarity] || '#fff' }}>{item.name}</div>
          <div className="muted">{item.category} &middot; tier {item.tier} &middot; {item.rarity}</div>
        </div>
      </div>
      <div className="details">
        <div className="row"><span className="lbl">Description</span><span>{item.description}</span></div>
        <div className="row"><span className="lbl">Recipe</span><span>{item.recipeType} &middot; {item.ingredientIds.length} ingredient(s)</span></div>
        <div className="row"><span className="lbl">Texture</span><span>{item.textureId} {item.textureReused ? <span className="badge reuse">reused</span> : null}</span></div>
        <div className="row"><span className="lbl">Inherited</span><span>{item.inheritedTraits.join(', ') || 'none'}</span></div>
        <div className="row"><span className="lbl">New traits</span><span>{item.newTraits.join(', ') || 'none'}</span></div>
        <div className="row"><span className="lbl">Discovered</span><span>{new Date(item.discoveredAt).toLocaleString()}</span></div>
        {item.connectionExplanation ? <div className="explain"><b>Connection:</b> {item.connectionExplanation}</div> : null}
        {item.textureExplanation ? <div className="explain"><b>Texture:</b> {item.textureExplanation}</div> : null}
      </div>

      <MiniGrid item={item} />
      <div className="muted" style={{ marginTop: 4 }}>Grid layout</div>

      <Section title="Parents (ingredients)">
        {parents.length === 0 ? <None /> : (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {parents.map((m, i) => <ItemIcon key={m.id + i} itemId={m.id} size={36} onClick={() => m.id.startsWith('custom:') && /* navigate */ undefined} />)}
          </div>
        )}
      </Section>

      <Section title="Children (uses this item)">
        {children.length === 0 ? <None /> : (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {children.map((c) => <ItemIcon key={c.id} itemId={c.id} size={36} />)}
          </div>
        )}
      </Section>

      <div className="row" style={{ marginTop: 12 }}>
        <button className="btn" onClick={() => game.toggleFavorite(item.id)}>
          {game.favorites.includes(item.id) ? 'Unfavorite' : 'Favorite'}
        </button>
        <button className="btn" onClick={() => game.placeItemAnySlot(item.id)}>Place in grid</button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div className="muted" style={{ marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}
function None() {
  return <div className="muted">none</div>;
}
